import { useQueryClient } from '@tanstack/react-query';
import {
  ApplyCaseManifestResponse,
  CaseAssignmentSchedulingOutcome,
  createProcedures,
  overrideStudy as overrideStudyApiCall,
} from 'api/study';
import { Procedure } from 'interfaces/procedure';
import { filter, forEach, includes, map, size } from 'lodash';
import { useEffect, useState } from 'react';
import { getBatches } from 'utils/batching';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';

import { CaseOrganizationPlan } from './caseOrganizationPlan';

interface CaseBatchMutationVariables {
  studyId: string;
  procedures: Procedure[];
  batchIndex: number;
}

interface UseCaseOrganizationExecutionInput {
  caseOrganizationPlan: CaseOrganizationPlan;
  labId: string;
  studyId: string;
}

const CASES_PER_BATCH = 50;

export const useCaseOrganizationExecution = ({
  caseOrganizationPlan,
  labId,
  studyId,
}: UseCaseOrganizationExecutionInput) => {
  const queryClient = useQueryClient();
  const [completedBatches, setCompletedBatches] = useState<number[]>([]);
  const [settledBatches, setSettledBatches] = useState<number[]>([]);
  const [failedCaseBatches, setFailedCaseBatches] = useState<CaseBatchMutationVariables[]>([]);
  const [overrideFailed, setOverrideFailed] = useState(false);
  const [totalBatches, setTotalBatches] = useState(0);
  const [failedSchedulingOutcomes, setFailedSchedulingOutcomes] = useState<CaseAssignmentSchedulingOutcome[]>([]);
  const [runningDialogOpen, setRunningDialogOpen] = useState(false);
  const completedBatchCount = completedBatches.length;
  const settledBatchCount = settledBatches.length;

  useEffect(() => {
    if (totalBatches > 0 && settledBatchCount === totalBatches && completedBatchCount > 0) {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['procedures'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['pendingSlides'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['pendingSlidesCount'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['studies', labId], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['study', studyId, labId], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['labUploadSummary', labId], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['labLostAndFound', labId], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['studyUploads'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['jobs'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['jobsFilteredIds'], exact: false }),
      ]);
    }
  }, [completedBatchCount, labId, queryClient, settledBatchCount, studyId, totalBatches]);

  const overrideStudyMutation = useMutationWithErrorSnackbar({
    mutationDescription: `override study ${studyId}`,
    mutationFn: (variables: { studyId: string; procedureLabels: string[]; batchIndex: number }) =>
      overrideStudyApiCall({ studyId: variables.studyId, procedureLabels: variables.procedureLabels }),
    onError: (error: any, variables: { studyId: string; procedureLabels: string[]; batchIndex: number }) => {
      setOverrideFailed(true);
      setSettledBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
      console.error(`Error occurred while overriding cases for study ${variables.studyId}:`, error);
    },
    onSuccess: (_data, variables) => {
      setCompletedBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
      setSettledBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
    },
  });

  const createProceduresMutation = useMutationWithErrorSnackbar({
    mutationDescription: `create procedures for study ${studyId}`,
    mutationFn: (variables: CaseBatchMutationVariables) =>
      createProcedures({ studyId: variables.studyId, procedures: variables.procedures }),
    onError: (error: any, variables) => {
      setFailedCaseBatches((previousBatches) => [
        ...filter(previousBatches, ({ batchIndex }) => batchIndex !== variables.batchIndex),
        variables,
      ]);
      setSettledBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
      console.error(
        `Error occurred while creating procedures for study ${variables.studyId}. batchIndex: ${variables.batchIndex}.
        error:`,
        error
      );
    },
    onSuccess: (data: ApplyCaseManifestResponse, variables) => {
      setFailedCaseBatches((previousBatches) =>
        filter(previousBatches, ({ batchIndex }) => batchIndex !== variables.batchIndex)
      );
      setFailedSchedulingOutcomes((previousOutcomes) => [
        ...previousOutcomes,
        ...filter(data.schedulingOutcomes, { status: 'failed' }),
      ]);
      setCompletedBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
      setSettledBatches((previousBatches) =>
        includes(previousBatches, variables.batchIndex) ? previousBatches : [...previousBatches, variables.batchIndex]
      );
    },
  });

  const resetExecutionState = () => {
    setCompletedBatches([]);
    setSettledBatches([]);
    setFailedCaseBatches([]);
    setOverrideFailed(false);
    setTotalBatches(0);
    setFailedSchedulingOutcomes([]);
  };

  const retryFailedCaseBatches = () => {
    const batchesToRetry = failedCaseBatches;
    const batchIndexesToRetry = map(batchesToRetry, 'batchIndex');

    setFailedCaseBatches([]);
    setSettledBatches((previousBatches) =>
      filter(previousBatches, (batchIndex) => !includes(batchIndexesToRetry, batchIndex))
    );
    forEach(batchesToRetry, (variables) => createProceduresMutation.mutate(variables));
  };

  const applyCaseOrganization = async () => {
    const overrideStudy = caseOrganizationPlan.executionMode === 'wholeStudyReplacement';
    setRunningDialogOpen(true);
    resetExecutionState();
    const caseBatches = getBatches<Procedure>(caseOrganizationPlan.requestPayload, CASES_PER_BATCH);
    const totalBatchesToSave = size(caseBatches);
    setTotalBatches(totalBatchesToSave + (overrideStudy ? 1 : 0));

    if (overrideStudy) {
      try {
        await overrideStudyMutation.mutateAsync({
          studyId,
          procedureLabels: map(caseOrganizationPlan.requestPayload, (currentDraftCase) => currentDraftCase.label || ''),
          batchIndex: totalBatchesToSave,
        });
      } catch (error) {
        console.error('Error occurred while overriding cases, skipping the rest of the batches');
        return;
      }
    }

    forEach(caseBatches, (batchCases, batchIndex) => {
      createProceduresMutation.mutate({ studyId, procedures: batchCases, batchIndex });
    });
  };

  return {
    applyCaseOrganization,
    completedBatchCount,
    createProceduresIsLoading: createProceduresMutation.isLoading,
    failedCaseBatchCount: failedCaseBatches.length,
    failedSchedulingCount: failedSchedulingOutcomes.length,
    overrideFailed,
    resetExecutionState,
    retryFailedCaseBatches,
    runningDialogOpen,
    setRunningDialogOpen,
    totalBatches,
  };
};
